import { Container, Graphics } from 'pixi.js';
import { VISUALS } from '../config.ts';
import type { Magnet } from '../simulation/Magnet.ts';

/**
 * Magnets: a disc standing on the table, with a polarity glyph.
 *
 * The halo around a magnet is drawn by the background shader, where its radius can be
 * derived from the real force law. What is left here is the object itself — shadow, body,
 * bevel, glyph — so this stays cheap Graphics and no filters are involved. Filters cost a
 * render-target switch per magnet, which is exactly the sort of thing that quietly eats a
 * mobile frame budget.
 */

const POSITIVE = 0xb42a36;
const NEGATIVE = 0x1550a8;
const POSITIVE_TOP = 0xff6b76;
const NEGATIVE_TOP = 0x5aa8ff;
const RING = 0xffffff;

/** How the player is currently touching a magnet, for the affordances below. */
export interface MagnetInteraction {
  magnet: Magnet | null;
  dragging: boolean;
  /** Long-press progress towards removal, 0..1. */
  holdProgress: number;
}

interface MagnetView {
  graphics: Graphics;
  /** What the body was last drawn with, so it is only redrawn when it changes. */
  drawnPolarity: number;
  drawnRadius: number;
  /**
   * Animation clocks, in seconds.
   *
   * These live here rather than on the Magnet because they are presentation state — and
   * because when they lived on the simulation they never advanced: the only place that
   * ticked them was World.step, which returns immediately unless a run is going, and
   * magnets can only be placed or flipped while planning. Every magnet sat pinned at the
   * first frame of its spawn animation for the whole phase.
   */
  age: number;
  flipAge: number;
}

export class MagnetRenderer {
  readonly container = new Container({ label: 'magnets' });

  private readonly views = new Map<string, MagnetView>();
  private readonly overlay = new Graphics();

  constructor() {
    this.container.addChild(this.overlay);
  }

  update(magnets: readonly Magnet[], interaction: MagnetInteraction, dt: number): void {
    const seen = new Set<string>();

    for (const magnet of magnets) {
      seen.add(magnet.id);
      let view = this.views.get(magnet.id);
      if (!view) {
        const graphics = new Graphics();
        // Below the overlay so the long-press ring always reads on top.
        this.container.addChildAt(graphics, 0);
        // A brand new magnet starts its spawn clock at zero and its flip clock at
        // infinity, so the pop plays once and the flip pop does not.
        view = {
          graphics,
          drawnPolarity: 0,
          drawnRadius: 0,
          age: 0,
          flipAge: Number.POSITIVE_INFINITY,
        };
        this.views.set(magnet.id, view);
      }

      view.age += dt;
      view.flipAge += dt;

      if (view.drawnPolarity !== magnet.polarity || view.drawnRadius !== magnet.radius) {
        // Polarity changing is how a flip reaches the renderer, so it is also when the
        // flip pop starts.
        if (view.drawnPolarity !== 0 && view.drawnPolarity !== magnet.polarity) {
          view.flipAge = 0;
        }
        drawMagnet(view.graphics, magnet);
        view.drawnPolarity = magnet.polarity;
        view.drawnRadius = magnet.radius;
      }

      view.graphics.position.set(magnet.position.x, magnet.position.y);

      // Spawn pop, flip pop, and a lift while dragging — all decaying exponentials so
      // they never linger.
      const spawn = Math.min(1, view.age / 0.16);
      const spawnScale = 0.6 + 0.4 * spawn * (2 - spawn);
      const flipPop = 0.26 * Math.exp(-view.flipAge * 13);
      const held = interaction.magnet === magnet && interaction.dragging ? 0.1 : 0;
      view.graphics.scale.set(spawnScale + flipPop + held);
    }

    for (const [id, view] of this.views) {
      if (seen.has(id)) continue;
      view.graphics.destroy();
      this.views.delete(id);
    }

    this.drawOverlay(interaction);
  }

  /** Long-press feedback: a ring that closes as removal approaches. */
  private drawOverlay(interaction: MagnetInteraction): void {
    const graphics = this.overlay;
    graphics.clear();

    const { magnet, holdProgress } = interaction;
    if (!magnet || holdProgress <= 0.02) return;

    const radius = magnet.radius + 16;
    graphics
      .arc(
        magnet.position.x,
        magnet.position.y,
        radius,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * holdProgress,
      )
      .stroke({ width: 6, color: 0xff9aa8, alpha: 0.5 + holdProgress * 0.5, cap: 'round' });
  }

  clear(): void {
    for (const view of this.views.values()) view.graphics.destroy();
    this.views.clear();
    this.overlay.clear();
  }
}

function drawMagnet(graphics: Graphics, magnet: Magnet): void {
  const accent = magnet.polarity === 1 ? POSITIVE : NEGATIVE;
  const top = magnet.polarity === 1 ? POSITIVE_TOP : NEGATIVE_TOP;
  const radius = magnet.radius;
  const shadowX = -VISUALS.lightDirection.x * VISUALS.shadowOffset;
  const shadowY = -VISUALS.lightDirection.y * VISUALS.shadowOffset;

  graphics.clear();

  graphics
    // Contact shadow.
    .circle(shadowX, shadowY, radius)
    .fill({ color: 0x000000, alpha: 0.5 })
    // A dark gasket between the magnet and the field.
    //
    // A magnet sits at the brightest point of its own potential, and bloom bleeds that
    // brightness straight over the top of it — without a break in tone the puck washes
    // out into the colour it is standing on.
    .circle(0, 0, radius + 9)
    .fill({ color: 0x05070e, alpha: 0.62 })
    // Solid body in the polarity colour, with a lit face inset inside it.
    //
    // Filled rather than dark: the board behind is a saturated field of the same two
    // colours, and a dark disc with a coloured rim disappears into it. A solid puck under
    // a white ring stays an object sitting on top of the picture.
    .circle(0, 0, radius)
    .fill(accent)
    .circle(0, 0, radius)
    .stroke({ width: 4.5, color: RING, alpha: 0.9 });

  // Bevel highlight on the side facing the lamp.
  const lightAngle = Math.atan2(VISUALS.lightDirection.y, VISUALS.lightDirection.x);
  // Kept faint: everything here goes through a bloom pass, and a magnet is a small
  // bright object on a saturated field — it blows out to a white disc very easily.
  graphics
    .arc(0, 0, radius - 11, lightAngle - 0.95, lightAngle + 0.95)
    .stroke({ width: 3, color: top, alpha: 0.5, cap: 'round' });

  // Polarity glyph, drawn as bars so no font has to load.
  const barLength = radius * 0.82;
  const barThickness = radius * 0.22;
  graphics.rect(-barLength / 2, -barThickness / 2, barLength, barThickness).fill(RING);
  if (magnet.polarity === 1) {
    graphics.rect(-barThickness / 2, -barLength / 2, barThickness, barLength).fill(RING);
  }
}
